import { X } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { Sitting, SittingDates } from '../api/exams'

export interface SittingDrawerProps {
  /** What is being edited: an existing Sitting, or a new one with nothing filled in. */
  sitting: Sitting
  /** True where this Sitting is not entered yet, so the name can be typed and the footer says Add. */
  adding: boolean
  saving: boolean
  error: string | null
  onSave: (name: string, dates: SittingDates) => void
  onClose: () => void
}

/**
 * One Sitting: its name and its three dates, in a panel over the grid.
 *
 * The Questions Manager's drawer, at a Sitting's scale: a panel over a dimmed grid, Escape
 * closes it, and the footer carries the save. The dates are the browser's own date fields,
 * which type-check a date better than any box this screen could draw.
 *
 * The name is typed once. It is what an Entitlement holds and expires against (app:R42),
 * so renaming a Sitting would orphan every pass sold for it; the dates are what move when
 * UAT UK moves them (app:R67). There is no Remove for the same reason.
 */
export function SittingDrawer({
  sitting,
  adding,
  saving,
  error,
  onSave,
  onClose,
}: SittingDrawerProps) {
  const [draft, setDraft] = useState<Sitting>(sitting)

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const title = adding ? 'Add a Sitting' : `Sitting ${sitting.name}`

  return (
    <>
      <div className="fixed inset-0 z-40 bg-ink-900/20" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="fixed top-0 right-0 z-50 flex h-full w-full max-w-xl flex-col border-l border-ink-100 bg-card shadow-xl"
      >
        <header className="flex items-center gap-3 border-b border-ink-100 p-4">
          <h2 className="font-heading text-h3 font-bold text-heading">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto rounded p-2 hover:bg-green-050"
          >
            <X aria-hidden size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4">
          <Field label="Sitting">
            <input
              className={`${input} ${adding ? '' : 'bg-page text-muted'}`}
              value={draft.name}
              readOnly={!adding}
              placeholder="October 2026"
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </Field>

          <Field label="Booking closes">
            <input
              type="date"
              className={input}
              value={draft.bookingClosesOn}
              onChange={(event) => setDraft({ ...draft, bookingClosesOn: event.target.value })}
            />
          </Field>

          <Field label="Tests from">
            <input
              type="date"
              className={input}
              value={draft.testsFrom}
              onChange={(event) => setDraft({ ...draft, testsFrom: event.target.value })}
            />
          </Field>

          <Field label="Tests to">
            <input
              type="date"
              className={input}
              value={draft.testsTo}
              onChange={(event) => setDraft({ ...draft, testsTo: event.target.value })}
            />
          </Field>
        </div>

        <footer className="border-t border-ink-100 p-4">
          {error && (
            <p role="alert" className="mb-3 text-red-700">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button type="button" className={quiet} onClick={onClose}>
              Discard
            </button>
            <button
              type="button"
              className={loud}
              disabled={saving}
              onClick={() =>
                onSave(draft.name.trim(), {
                  bookingClosesOn: draft.bookingClosesOn,
                  testsFrom: draft.testsFrom,
                  testsTo: draft.testsTo,
                })
              }
            >
              {saving ? 'Saving...' : adding ? 'Add Sitting' : 'Save changes'}
            </button>
          </div>
        </footer>
      </aside>
    </>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mt-3 block first:mt-0">
      <span className="font-bold text-heading">{label}</span>
      {children}
    </label>
  )
}

const input = 'mt-1 w-full rounded border border-ink-100 px-3 py-2'

const quiet =
  'inline-flex items-center gap-2 rounded-md border border-green-800 px-3 py-1.5 font-bold text-green-800 hover:bg-green-050 disabled:opacity-55'

const loud =
  'inline-flex items-center gap-2 rounded-md bg-green-800 px-4 py-2 font-bold text-white hover:bg-green-900 disabled:opacity-55'
